import { useState } from "react";
import { Link } from "react-router-dom";
import Layout from "@/components/feature/Layout";
import PageHero from "@/components/base/PageHero";
import SectionHeader from "@/components/base/SectionHeader";
import { insights, insightFilters } from "@/mocks/insights";

export default function Insights() {
  const [activeFilter, setActiveFilter] = useState("All");

  const filteredInsights =
    activeFilter === "All"
      ? insights
      : insights.filter((insight) => insight.categories.includes(activeFilter));

  return (
    <Layout>
      <PageHero
        title="Insights"
        subtitle="Thought Leadership"
        description="Explore practical perspectives on enterprise IT engineering, database and platform services, application integration, Microsoft business platforms, and technical delivery teams."
        size="md"
      />

      <section className="section-padding bg-background-50">
        <div className="container-page">
          <SectionHeader
            title="Perspectives for Technical and Program Teams"
            description="Practical, capability-aligned guidance for organizations modernizing infrastructure, operating mission-critical data platforms, and building effective delivery teams."
          />

          {/* Filters */}
          <div className="flex flex-wrap justify-center gap-2.5 mb-12">
            {["All", ...insightFilters].map((filter) => (
              <button
                key={filter}
                onClick={() => setActiveFilter(filter)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeFilter === filter
                    ? "bg-primary-500 text-background-50"
                    : "bg-white text-foreground-700 border border-background-200 hover:border-primary-300 hover:text-primary-700"
                }`}
              >
                {filter}
              </button>
            ))}
          </div>

          {/* Insight cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredInsights.map((insight) => (
              <article
                key={insight.slug}
                className="card-base card-hover overflow-hidden flex flex-col group"
              >
                <div
                  className="relative h-40 flex items-center justify-center bg-gradient-to-br from-primary-800 via-primary-700 to-accent-700 overflow-hidden"
                  role="img"
                  aria-label={`${insight.title} visual`}
                >
                  <i className={`${insight.icon} text-white/90 text-5xl`} aria-hidden="true"></i>
                  <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/20"></div>
                </div>

                <div className="p-6 flex flex-col flex-1">
                  <div className="flex items-center gap-2 mb-3">
                    <span className="text-xs font-semibold uppercase tracking-wider px-2.5 py-1 rounded-full bg-secondary-100 text-secondary-800">
                      {insight.category}
                    </span>
                    <span className="text-xs text-foreground-500 flex items-center gap-1">
                      <i className="ri-time-line" aria-hidden="true"></i>
                      {insight.readingTime}
                    </span>
                  </div>

                  <h3 className="text-lg font-bold text-foreground-900 leading-snug mb-3 group-hover:text-primary-700 transition-colors">
                    {insight.title}
                  </h3>

                  <p className="text-sm text-foreground-600 leading-relaxed mb-5 flex-1">
                    {insight.summary}
                  </p>

                  <Link
                    to={`/resources/insights/${insight.slug}`}
                    className="inline-flex items-center justify-center px-5 py-2.5 rounded-md font-semibold text-sm tracking-wide bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors whitespace-nowrap"
                  >
                    Read Insight
                    <i className="ri-arrow-right-line ml-2" aria-hidden="true"></i>
                  </Link>
                </div>
              </article>
            ))}
          </div>

          {filteredInsights.length === 0 && (
            <div className="text-center py-16">
              <p className="text-sm text-foreground-500">
                No insights found in this category yet.
              </p>
            </div>
          )}
        </div>
      </section>
    </Layout>
  );
}